<script>
    /**
     * Translation settings modal, shared by every app that offers translation.
     */
    import { Modal } from '@repo/ui';
    import { showTranslationSettingsModal, showLoader } from '@repo/stores/Actions';
    import { Form } from '@repo/ui';
    import { FormRow } from '@repo/ui';
    import { DynamicTitle } from '@repo/ui';
    import { translationLanguage, translationActive } from '@repo/stores/Language';
    import { FloatingModal } from '@repo/ui';
    import { TextInput } from '@repo/ui';
    import { LanguageSelectorCard } from '@repo/ui';
    import { postHogCapture } from '@repo/config/postHogHelper';
    import { translationLanguages, isEnglish } from './languages.js';
    import { readTranslationSettings } from './settings.js';

    let {
        user = null,
        onSave = null,
        languages: providedLanguages = null,
    } = $props();

    let curLang = $state('en');
    let armLang = null;
    let modalMessage = $state(null);
    let isShowConfirmModal = $state(false);
    let searchQuery = $state('');

    $effect(() => {
        curLang = readTranslationSettings(user).language;
    });

    /** Normalise a catalogue entry to the shape LanguageSelectorCard expects. */
    function normalise(list) {
        const mapped = (list ?? []).map((l) => ({
            displayName: l.displayName,
            name: l.language,
            localisedName: l.localisedName,
            textDirection: l.textDirection,
        }));

        mapped.sort((a, b) => a.displayName.localeCompare(b.displayName));

        // English leads the list — it is the "translation off" option.
        const en = mapped.find((l) => l.name === 'en');
        return en ? [en, ...mapped.filter((l) => l.name !== 'en')] : mapped;
    }

    const allLanguages = $derived(normalise(providedLanguages ?? translationLanguages));

    const suggestedLanguages = $derived.by(() => {
        const browserLanguages = typeof navigator !== 'undefined' && navigator.languages
            ? navigator.languages.map((lang) => lang.split('-')[0].toLowerCase())
            : [];

        return allLanguages.filter(
            (lang) => lang.name === curLang || browserLanguages.includes(lang.name)
        );
    });

    const filteredLanguages = $derived.by(() => {
        if (!searchQuery) return [...suggestedLanguages, ...allLanguages];

        return allLanguages.filter(
            (language) =>
                language.displayName.toLowerCase().includes(searchQuery) ||
                language.name.toLowerCase().includes(searchQuery) ||
                language.localisedName?.toLowerCase().includes(searchQuery)
        );
    });

    function close() {
        showTranslationSettingsModal.set(false);
        setTimeout(() => {
            // Optional chaining matters: not every app's header renders this
            // button, and an unguarded call throws on the way out of the modal.
            document.querySelector('.menu-btn[title="Main Menu"]')?.focus();
        }, 600);
    }

    function handleTranslationLanguageChange(event) {
        armLang = event.detail.name;

        if (armLang === curLang) {
            return;
        }

        const langDisplayName = allLanguages.find((l) => l.name === armLang)?.displayName || '';
        modalMessage = `You are about to change the language to '${langDisplayName}'. This will translate the interface and create translation rollovers on content.<br>The page will refresh once you confirm.`;
        isShowConfirmModal = true;

        postHogCapture('translation_language_change', { lang: armLang });
    }

    async function handleLanguageChangeConfirm() {
        isShowConfirmModal = false;
        showLoader.set('Updating language settings...');

        const active = !isEnglish(armLang);
        translationLanguage.set(armLang);
        translationActive.set(active);

        try {
            await onSave?.({ translationLanguage: armLang, translationActive: active });
            postHogCapture('translation_language_change_confirmed', { lang: armLang });
        } catch (err) {
            // The stores are session-persisted, so the choice still applies for
            // this session even if it could not be written to the user doc.
            console.error('[translation] failed to save language settings', err);
        }

        document.defaultView.location.reload();
    }

    function handleLanguageChangeCancel() {
        armLang = null;
        isShowConfirmModal = false;

        // @ts-ignore
        document.querySelector('div[name="translationlanguage"] button')?.click();
    }

    function handleSearch(event) {
        searchQuery = event.detail.value.toLowerCase().trim();
    }
</script>

{#if $showTranslationSettingsModal}
    <Modal tabs={['Translation Settings']} showFooter={false} on:close={close}>
        {#snippet body()}
                <Form >
                <DynamicTitle label="Choose a Language" icon="translate" as="h3" />
                <FormRow>
                    <h4>To Activate Translations:</h4>
                    <p>Click on your preferred language. You will be asked to confirm the selected language, and the screen will briefly reload. Once the page has reloaded, select your book title and the desired activity. </p>

                    <h4>To View Translated Text:</h4>
                    <p>Simply hover your mouse pointer over a block of text to view the translation. You may also use your keyboard (press TAB) to navigate to the block of text and press &lt; alt &gt; + &lt; t &gt; key.</p>

                    <h4>Deactivation of the Translation Feature:</h4>
                    <p>Turn the translation rollover feature ON and OFF by toggling the toggle box found at the top right of the screen.</p>
                </FormRow>

                <FormRow label="Search for a Language" labelFor="translationlanguageSearch">
                    <div class="basic-search-container">
                        <TextInput
                            name="basic-search"
                            type="search"
                            placeholder="Search for a Language"
                            on:keyup={handleSearch}
                        />
                    </div>
                </FormRow>

                {#if searchQuery}
                    <!-- Single combined list during search -->
                    <div class="language-list">
                        {#if filteredLanguages.length > 0}

                            {#each filteredLanguages as language (language.name)}
                                <LanguageSelectorCard
                                    name={language.name}
                                    displayName={language.displayName}
                                    localisedName={language.localisedName}
                                        selected={language.name === curLang}
                                    on:languageSelected={handleTranslationLanguageChange}
                                />
                            {/each}
                        {:else}
                            <p>No languages match your search.</p>
                        {/if}
                    </div>
                {:else}
                    <!-- Two separate lists when not searching -->
                    <FormRow label="Suggested Languages">
                        <div class="language-list">
                            {#each suggestedLanguages as language (language.name)}
                                <LanguageSelectorCard
                                    name={language.name}
                                    displayName={language.displayName}
                                    localisedName={language.localisedName}
                                        selected={language.name === curLang}
                                    on:languageSelected={handleTranslationLanguageChange}
                                />
                            {/each}
                        </div>
                    </FormRow>

                    <FormRow label="All">
                        <div class="language-list">
                            {#each allLanguages as language (language.name)}
                                <LanguageSelectorCard
                                    name={language.name}
                                    displayName={language.displayName}
                                    localisedName={language.localisedName}
                                    textDirection={language.textDirection}
                                    selected={language.name === curLang}
                                    on:languageSelected={handleTranslationLanguageChange}
                                />
                            {/each}
                        </div>
                    </FormRow>
                {/if}

                {#if isShowConfirmModal}
                    <FloatingModal
                        on:confirm={handleLanguageChangeConfirm}
                        on:cancel={handleLanguageChangeCancel}
                        title="Confirm Language Change"
                        message={modalMessage}
                    />
                {/if}
            </Form>
            {/snippet}
    </Modal>
{/if}

<style>
    .language-list {
        display: flex;
        flex-direction: column;
        gap: 10px;
        width: 100%;
    }

    .basic-search-container {
        display: flex;
        align-items: center;
        width: 100%;
    }

    .basic-search-container :global(.text-input) {
        background-color: rgba(255, 255, 255, 0.2);
        border-radius: 0.25rem;
    }

    .basic-search-container :global(.text-input input) {
        display: flex;
        flex-grow: 10;
        width: 100%;
        height: 1.5em;
        background: none;
        border: none;
        outline: none;
        color: #ddd;
    }

    :global(.modal-content) {
        overflow: auto;
        scrollbar-width: none;
    }

    :global(.form-row label), :global(.dynamic-title, .dynamic-title span) {
        font-weight: 400 !important;
    }

    :global(.form-row >.basic-search-container > .text-input) {
        max-width: unset;
    }

    h4 {
        font-weight:600;
        font-size:0.75rem;
        margin:1rem 0 0.5rem;
        color:rgba(255, 255, 255, 0.6);
    }

    p {
        font-size: 0.75rem;
        color: rgba(255, 255, 255, 0.6);
        margin-bottom: 0.5rem;
    }
</style>
